import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardContent,
  Heading,
  Text,
  Button,
  Flex,
  LoadingOrbit,
  TagBadge,
  Banner,
  useToast,
} from '@chatsdet/ui';
import { GithubLogo, ArrowsClockwise, Terminal, WarningCircle, Play } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import type { Runbook } from '../../../features/runbooks/types';
import type { LinkedRepo } from './PropertyCodeTab.types';
import { RunbookTriggerDialog } from './RunbookTriggerDialog';
import { RunbookRunDetailDrawer } from './RunbookRunDetailDrawer';
import { RunbookRunHistory } from './RunbookRunHistory';

interface PropertyCodeRunbooksSectionProps {
  propertyId: string;
  linkedRepos: LinkedRepo[];
}

export function PropertyCodeRunbooksSection({
  propertyId,
  linkedRepos,
}: PropertyCodeRunbooksSectionProps) {
  const qc = useQueryClient();
  const { toast } = useToast();
  // Execution is gated server-side (`RUNBOOKS_FEATURE_FLAG` in
  // apps/api/src/runbooks/routes.ts): the Run button is always shown, and if
  // the flag is off the run mutation surfaces the server's
  // `RUNBOOK_EXECUTION_NOT_ENABLED` userError as a toast. There is no client
  // feature key for this today (`packages/types/src/user-features.ts` has no
  // `runbooks` entry), so pre-hiding the button would require a
  // packages/types change outside this task's scope.
  const [triggeringRunbook, setTriggeringRunbook] = React.useState<Runbook | null>(null);
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null);

  const runbooksQuery = useQuery({
    queryKey: ['property-runbooks', propertyId],
    queryFn: async (): Promise<{ ok: boolean; items: Runbook[] }> => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/runbooks`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch runbooks');
      return res.json();
    },
  });

  const rescanMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/org/properties/${propertyId}/runbooks/rescan`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Failed to queue rescan');
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-runbooks', propertyId] });
      toast.success('Rescan queued — runbooks update shortly');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to queue rescan');
    },
  });

  const repoById = new Map(linkedRepos.map((r) => [r.id, r]));
  const runbooks = runbooksQuery.data?.items ?? [];

  return (
    <>
    <Card>
      <CardHeader>
        <Flex align="center" justify="between">
          <div>
            <Heading level={3}>Runbooks</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Operational commands discovered from <code>cofactor/runbooks/*.yml</code> in linked
              repositories
            </Text>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onPress={() => rescanMutation.mutate()}
            iconLeft={<ArrowsClockwise size={16} />}
            isDisabled={rescanMutation.isPending}
          >
            {rescanMutation.isPending ? 'Queuing…' : 'Rescan'}
          </Button>
        </Flex>
      </CardHeader>
      <CardContent>
        {runbooksQuery.isLoading ? (
          <Flex align="center" justify="center" className="py-8">
            <LoadingOrbit size="md" />
          </Flex>
        ) : runbooksQuery.isError ? (
          // Distinct from the empty state on purpose: "we couldn't load these"
          // and "this repo has none" are different facts, and showing the empty
          // copy on a failed fetch would assert something we don't know.
          <Banner variant="error">
            <Flex align="center" gap="sm">
              <WarningCircle size={16} className="shrink-0" />
              <Text size="sm">Couldn&apos;t load runbooks. Reload the page to try again.</Text>
            </Flex>
          </Banner>
        ) : runbooks.length > 0 ? (
          <div className="space-y-2">
            {runbooks.map((runbook) => {
              const repo = repoById.get(runbook.propertyGithubRepoId);
              return (
                <div
                  key={runbook.id}
                  className="py-3 border-b border-border last:border-0"
                >
                  {runbook.parseError ? (
                    <Banner variant="error">
                      <Flex align="start" justify="between" gap="md" className="w-full">
                        <Flex direction="column" gap="xs">
                          <Flex align="center" gap="sm">
                            <WarningCircle size={16} className="shrink-0" />
                            <span className="font-medium text-sm">{runbook.slug}</span>
                            {repo && (
                              <Flex align="center" gap="xs" className="text-xs opacity-80">
                                <GithubLogo size={12} />
                                {repo.repoFullName}
                              </Flex>
                            )}
                          </Flex>
                          <Text size="xs" className="font-mono opacity-90">
                            {runbook.filePath}
                          </Text>
                          <Text size="sm">{runbook.parseError}</Text>
                        </Flex>
                        <div
                          className="shrink-0"
                          title={`Can't run — this file failed to parse: ${runbook.parseError}`}
                        >
                          <Button variant="outline" size="sm" iconLeft={<Play size={14} />} isDisabled>
                            Run
                          </Button>
                        </div>
                      </Flex>
                    </Banner>
                  ) : (
                    <Flex align="start" justify="between" gap="md">
                      <Flex align="start" gap="sm">
                        <Terminal size={20} className="text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <Flex align="center" gap="sm">
                            <span className="font-medium">{runbook.name}</span>
                            {runbook.destructive && (
                              <TagBadge name="Destructive" color="red" hideDot className="text-xs" />
                            )}
                          </Flex>
                          <Flex align="center" gap="sm" className="mt-0.5">
                            <Text size="xs" className="text-muted-foreground font-mono">
                              {runbook.slug}
                            </Text>
                            <Text size="xs" className="text-muted-foreground font-mono">
                              {runbook.filePath}
                            </Text>
                            {repo && (
                              <Flex align="center" gap="xs" className="text-xs text-muted-foreground">
                                <GithubLogo size={12} />
                                {repo.repoFullName}
                              </Flex>
                            )}
                          </Flex>
                          {runbook.description && (
                            <Text size="sm" className="text-muted-foreground mt-1 line-clamp-2">
                              {runbook.description}
                            </Text>
                          )}
                        </div>
                      </Flex>
                      <Button
                        variant="outline"
                        size="sm"
                        iconLeft={<Play size={14} />}
                        onPress={() => setTriggeringRunbook(runbook)}
                        className="shrink-0"
                      >
                        Run
                      </Button>
                    </Flex>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <Flex direction="column" align="center" gap="md" className="py-8">
            <div className="p-3 bg-muted rounded-full">
              <Terminal size={24} className="text-muted-foreground" />
            </div>
            <Text size="sm" className="text-muted-foreground text-center max-w-sm">
              No runbooks found. Add a <code>cofactor/runbooks/&lt;slug&gt;.yml</code> file to a
              linked repository&apos;s default branch, then rescan.
            </Text>
          </Flex>
        )}
      </CardContent>
    </Card>

      <RunbookRunHistory propertyId={propertyId} onSelectRun={setSelectedRunId} />

      <RunbookTriggerDialog
        runbook={triggeringRunbook}
        propertyId={propertyId}
        onOpenChange={(open) => {
          if (!open) setTriggeringRunbook(null);
        }}
        onRunStarted={(run) => setSelectedRunId(run.id)}
      />

      <RunbookRunDetailDrawer runId={selectedRunId} onOpenChange={() => setSelectedRunId(null)} />
    </>
  );
}
